<template>
  <form
      @submit.prevent="submitCredit"
      autocomplete="off"
      class="w-full max-h-screen bg-background flex flex-col relative"
      :class="{ 'overflow-y-hidden': isBusy }"
  >
    <div
        v-show="isBusy"
        class="absolute inset-0 bg-faint-white flex items-center justify-center z-10"
    >
      <spinner :color="'text-black'" :size="10"/>
    </div>
    <page-header :title="pageTitle" :info="getCreditNo" :enableLogicalBack="true" back-to="invoice">

      <SplitButton
          v-if="credit_id && creditStatusItems.length > 0"
          label="Set Status"
          :model="creditStatusItems"
          class="status-split-button mb-2"
          style="height: 35px;top:4px"
      ></SplitButton>

      <!-- Current Step Buttons -->
      <div v-for="button in currentStepButtons" :key="button.label" class="inline-block">
        <AppButton
            :color="button.color"
            :label="button.label"
            class="ml-2"
            @click="button.action"
            :icon="!!button.icon"
        >
          <template v-slot:icon v-if="button.icon">
            <Icon :iconType="button.icon" class="mr-2"/>
          </template>
        </AppButton>
      </div>

    </page-header>

    <main class="pr-6 pl-6 flex-1 overflow-y-scroll">
      <div class="flex gap-4 mt-6">
        <!-- Sold To Section -->
        <div class="w-2/12 bg-white p-4 rounded flex flex-col justify-between">
          <div>
            <div class="flex items-center mb-2">
              <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">
                Sold To <span class="text-red-400">*</span>
              </h3>
              <hr class="flex-1 border-primary ml-4" />
            </div>
            <div>
              <ContactAutocomplete
                  class="w-full"
                  v-model="sold_to"
                  @contactSelected="handleSoldToSelected"
                  @contactCleared="handleSoldToCleared"
                  :forceSelection="true"
              />
            </div>
            <div class="w-full flex">
              <ContactInfoCard :contact="sold_to"></ContactInfoCard>
            </div>
          </div>
          <div class="flex justify-end mt-4">
            <AppOutlinedButton
                label="Add New"
                color="indigo"
                @click="addNewContact"
                :icon="true"
            >
              <template v-slot:icon>
                <Icon iconType="add" class="mr-2" />
              </template>
            </AppOutlinedButton>
          </div>
        </div>

        <!-- Invoice To Section -->
        <div class="w-2/12 bg-white p-4 rounded flex flex-col justify-between">
          <div>
            <div class="flex items-center mb-2">
              <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">
                Invoice To <span class="text-red-400">*</span>
              </h3>
              <hr class="flex-1 border-primary ml-4" />
            </div>
            <div>
              <ContactAutocomplete
                  class="w-full"
                  v-model="invoice_to"
                  @contactSelected="handleInvoiceToSelected"
                  @contactCleared="handleInvoiceToCleared"
                  :forceSelection="true"
              />
            </div>
            <div class="w-full flex">
              <ContactInfoCard :contact="invoice_to"></ContactInfoCard>
            </div>
          </div>
          <div class="flex justify-between items-center mt-4">
            <span class="uppercase text-sm font-semibold">Status:</span>
            <span
                :class="getStatusClass(credit.status)"
                class="px-3 py-1 text-sm font-semibold rounded"
            >
      {{ (credit.status || 'N/A').toUpperCase() }}
    </span>
          </div>
        </div>

        <!-- Delivery To Section -->
        <div class="w-2/12 bg-white p-4 rounded flex flex-col justify-between">
          <div>
            <div class="flex items-center mb-2">
              <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">
                Delivery To <span class="text-red-400">*</span>
              </h3>
              <hr class="flex-1 border-primary ml-4" />
            </div>
            <div>
              <ContactAutocomplete
                  class="w-full"
                  v-model="delivery_to"
                  @contactSelected="handleDeliveryToSelected"
                  @contactCleared="handleDeliveryToCleared"
                  :forceSelection="true"
              />
            </div>
            <div class="w-full flex">
              <ContactInfoCard :contact="delivery_to"></ContactInfoCard>
            </div>
          </div>
        </div>

        <!-- Set Details Section -->
        <div class="w-6/12 bg-white p-4 rounded flex flex-col">
          <div class="flex items-center mb-2">
            <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">
              Set Details
            </h3>
            <hr class="flex-1 border-primary ml-4" />
          </div>
          <div class="grid grid-cols-3 gap-2">
            <div class="col-span-2 grid grid-cols-2 gap-2">
              <div class="flex flex-col px-5 mb-3">
                <label class="uppercase font-semibold text-sm">Credit Date <span class="text-red-400">*</span></label>
                <Calendar
                    v-model="credit.credit_date"
                    dateFormat="dd/mm/yy"
                    :showIcon="true"
                    :minDate="earliestPickableDate"
                    :disabled="isCreditLocked || isInvoiceCorrectionCredit"
                />
                <span v-if="isInvoiceCorrectionCredit" class="text-xs text-secondary mt-1">
                  Set automatically to the day this credit note is issued.
                </span>
              </div>
              <div v-if="!isInvoiceCorrectionCredit" class="flex flex-col px-5 mb-3">
                <label class="uppercase font-semibold text-sm">Credit Expiry Date <span class="text-red-400">*</span></label>
                <Calendar
                    v-model="credit.credit_expiry_date"
                    dateFormat="dd/mm/yy"
                    :showIcon="true"
                    :minDate="earliestPickableDate"
                    :disabled="isCreditLocked"
                />
              </div>
              <div class="col-span-2 flex flex-col px-5 mb-3">
                <label class="uppercase font-semibold text-sm">Credit Notes</label>
                <textarea
                    v-model="credit.notes"
                    :disabled="isCreditLocked"
                    class="border rounded bg-gray-100 border-gray-400 hover:border-primary focus:border-primary py-3 px-4 focus:shadow w-full"
                    placeholder="Notes..."
                    rows="3"
                ></textarea>
              </div>
              <div class="col-span-2 flex flex-col px-5 mb-3">
                <label class="uppercase font-semibold text-sm">Internal Notes</label>
                <textarea
                    v-model="credit.internal_notes"
                    class="border rounded bg-gray-100 border-gray-400 hover:border-primary focus:border-primary py-3 px-4 focus:shadow w-full"
                    placeholder="Internal Notes..."
                    rows="3"
                ></textarea>
              </div>
            </div>
            <div class="col-span-1 flex flex-col gap-2">
              <div class="flex flex-col px-5 mb-3">
                <label class="uppercase font-semibold text-sm">Invoice No.</label>
                <input
                    :value="effectiveCreditSource === 'open' ? 'n/a for open credit' : credit.invoice_no"
                    class="bge-input bge-input-spacing rounded"
                    :class="{ 'text-gray-500 italic': effectiveCreditSource === 'open' }"
                    type="text"
                    readonly
                />
              </div>
              <div class="flex flex-col px-5 mb-3">
                <label class="uppercase font-semibold text-sm">Credit Type <span class="text-red-400">*</span></label>
                <select v-model="credit.credit_type" :disabled="isCreditLocked" class="bge-input bge-input-spacing rounded">
                  <option value="REFUND">REFUND</option>
                  <option value="DISCOUNT">DISCOUNT</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Select Products -->
      <div class="w-full bg-white mt-6 p-5 pt-1 rounded shadow-md relative">
        <div class="mt-6">
          <div class="flex items-center mb-2">
            <h3
                class="uppercase text-lg font-semibold text-primary tracking-wide"
            >
              Select Products
            </h3>
            <hr class="flex-1 border-primary ml-4"/>

            <AppButton v-if="canAddProducts && effectiveCreditSource === 'open'"
                       color="green"
                       label="Add Credit Amount"
                       @click="showAddCreditAmount"
                       :icon=true
                       class="ml-4">
              <template v-slot:icon>
                <Icon iconType="add" class="mr-2"/>
              </template>
            </AppButton>

          </div>
          <p v-if="effectiveCreditSource === 'open'" class="text-sm text-secondary mb-4">
            This Open Credit can be applied against the customer's future invoices in full or in
            part, any number of times, until it expires on {{ formattedCreditExpiryDate }}.
          </p>
          <p v-if="isDraftOpenCredit" class="text-sm text-orange-700 bg-orange-50 border border-orange-200 rounded p-3 mb-4">
            This credit is still a <strong>draft</strong>, so it cannot be applied to any invoice yet.
            {{ canActivateOpenCredit ? "Click Save & Activate to make it available." : "Add a credit amount, then click Save & Activate." }}
          </p>
          <OrderItems :credit_id="credit_id"
                      :credit="credit"
                      :creditLimit="creditNoteLimit"
                      ref="orderItemRef"
                      @changed="handleCreditItemChanged"
                      :initialProducts="credit.credit_items">
          </OrderItems>
        </div>
      </div>

      <!-- Credit Note Summary -->
      <div v-if="isInvoiceCorrectionCredit" class="w-full bg-white mt-6 p-5 rounded shadow-md relative">
        <div class="flex items-center mb-2">
          <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">Credit Note</h3>
          <hr class="flex-1 border-primary ml-4"/>
        </div>
        <div class="flex flex-col items-end">
          <div class="flex justify-between w-1/3 py-1">
            <span class="font-semibold">Original Invoice Value</span>
            <span>{{ formatCurrency(originalInvoiceValue) }}</span>
          </div>
          <div class="flex justify-between w-1/3 py-1">
            <span class="font-semibold">Total Deductions</span>
            <span>{{ formatCurrency(totalDeductions) }}</span>
          </div>
          <div class="flex justify-between w-1/3 py-1 border-t border-primary mt-1 pt-2">
            <span class="font-semibold">Outstanding Balance</span>
            <span>{{ formatCurrency(outstandingBalance) }}</span>
          </div>
        </div>
      </div>

      <!-- Order Cost -->
      <div class="w-full bg-white mt-6 p-5 pt-1 rounded shadow-md relative mb-6">
        <div class="mt-6">
          <ProductSummary
              v-if="credit"
              :products="getOrderItems()"
              :initialDuty="credit.duty"
              :initialShippingCost="credit.shipping_cost"
              :initialShippingTaxCode="credit.shipping_tax_code"
              :initialShippingTaxRate="credit.shipping_tax_rate"
              @update-duty="updateCreditValue('duty', $event)"
              @update-shipping-cost="updateCreditValue('shipping_cost', $event)"
              @update-shipping-tax-code="updateCreditValue('shipping_tax_code', $event)"
              @update-shipping-tax-rate="updateCreditValue('shipping_tax_rate', $event)"
          />
        </div>
      </div>

      <!-- Remaining Balance -->
      <div v-if="credit_id && effectiveCreditSource === 'invoice_correction'" class="w-full bg-white mt-6 p-5 rounded shadow-md relative mb-6">
        <div class="flex items-center mb-2">
          <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">Credit Note Summary</h3>
          <hr class="flex-1 border-primary ml-4"/>
        </div>
        <div v-if="!creditNoteApplication" class="flex flex-col items-end">
          <div class="flex justify-between items-baseline gap-x-4 w-2/3 py-1">
            <span class="font-semibold whitespace-nowrap">Credit Note Status</span>
            <span class="text-right">Not yet issued</span>
          </div>
          <div class="flex justify-between items-baseline gap-x-4 w-2/3 py-1">
            <span class="font-semibold whitespace-nowrap">Amount to be applied to {{ credit.invoice_no }}</span>
            <span class="text-right">{{ formatCurrency(creditNoteRequestedAmount) }}</span>
          </div>
        </div>
        <div v-else class="flex flex-col items-end">
          <div class="flex justify-between items-baseline gap-x-4 w-2/3 py-1">
            <span class="font-semibold whitespace-nowrap">Credit Note Status</span>
            <span class="text-right">Issued on {{ $moment.unix(creditNoteApplication.applied_at).format("DD-MM-YYYY") }} by {{ creditNoteApplication.applied_by }}</span>
          </div>
          <div class="flex justify-between items-baseline gap-x-4 w-2/3 py-1">
            <span class="font-semibold whitespace-nowrap">Applied to {{ creditNoteApplication.invoice_no }}</span>
            <span class="text-right">
              {{ formatCurrency(creditNoteApplication.amount) }}<template v-if="creditNoteAppliedAmountReduced"> (reduced from {{ formatCurrency(creditNoteRequestedAmount) }} to match the amount outstanding)</template>
            </span>
          </div>
          <div class="flex justify-between items-baseline gap-x-4 w-2/3 py-1">
            <span class="font-semibold whitespace-nowrap">Invoice outstanding after credit</span>
            <span class="text-right">{{ formatCurrency(linkedInvoice ? linkedInvoice.outstanding_balance : 0) }}</span>
          </div>
        </div>
      </div>

      <div v-if="credit_id && effectiveCreditSource === 'open'" class="w-full bg-white mt-6 p-5 rounded shadow-md relative mb-6">
        <div class="flex items-center mb-2">
          <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">Open Credit Balance</h3>
          <hr class="flex-1 border-primary ml-4"/>
        </div>
        <div class="flex flex-col items-end">
          <div class="flex justify-between w-1/3 py-1">
            <span class="font-semibold">Open Credit Total</span>
            <span>{{ formatCurrency(creditNoteRequestedAmount) }}</span>
          </div>
          <div class="flex justify-between w-1/3 py-1">
            <span class="font-semibold">Used so far</span>
            <span>{{ formatCurrency(openCreditApplicationsTotal) }}</span>
          </div>
          <div class="flex justify-between w-1/3 py-1">
            <span class="font-semibold">Remaining Balance</span>
            <span>{{ formatCurrency(credit.remaining_balance) }}</span>
          </div>
          <div class="flex justify-between w-1/3 py-1">
            <span class="font-semibold">Expires</span>
            <span>{{ credit.credit_expiry_date }}</span>
          </div>
        </div>

        <div v-if="credit.applications && credit.applications.length > 0" class="mt-4">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b-2 border-primary text-left">
                <th class="py-2">Date</th>
                <th class="py-2">Invoice No</th>
                <th class="py-2 text-right">Amount</th>
                <th class="py-2">Applied by</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="application in openCreditApplicationsSorted" :key="application.invoice_id + application.applied_at" class="border-b border-gray-200">
                <td class="py-1">{{ $moment.unix(application.applied_at).format("DD-MM-YYYY") }}</td>
                <td class="py-1">{{ application.invoice_no }}</td>
                <td class="py-1 text-right">{{ formatCurrency(application.amount) }}</td>
                <td class="py-1">{{ application.applied_by }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </main>

    <portal to="overlay-outlet">
      <PDFViewer :showing="pdfViewerData.isShowingViewer" :pdfBlob="pdfViewerData.pdfData"
                 :documentName="pdfViewerData.documentName" @close="closeViewer"/>
    </portal>

    <portal to="overlay-outlet">
      <panel
          :showing="issueChoicePanelOpen"
          @close="issueChoicePanelOpen = false"
          title="Issue Credit Note"
      >
        <div v-if="issuePreview" class="flex flex-col gap-4">
          <p class="text-sm text-secondary">
            This credit note was created for {{ formatCurrency(issuePreview.requestedAmount) }}, but the invoice
            currently only has {{ formatCurrency(issuePreview.invoiceOutstandingBalance) }} outstanding — other
            credit activity has reduced it since this credit note was created or last edited. Choose how to issue it:
          </p>

          <AppButton
              color="blue"
              :label="`Match Current Outstanding (${formatCurrency(issuePreview.invoiceOutstandingBalance)})`"
              @click="doIssueCreditNote(true)"
          />
          <p class="text-xs text-secondary -mt-3">
            Reduces this credit note's line items to match what the invoice actually still owes.
          </p>

          <AppButton
              color="orange"
              :label="`Issue As Originally Created (${formatCurrency(issuePreview.requestedAmount)})`"
              @click="doIssueCreditNote(false)"
          />
          <p class="text-xs text-secondary -mt-3">
            Ignores the other credit activity and issues the full original amount — the invoice's
            outstanding balance will floor at £0.00 rather than go negative.
          </p>
        </div>
      </panel>
    </portal>

    <portal to="overlay-outlet">
      <panel
          :showing="addCreditAmountPanelOpen"
          @close="closeAddCreditAmount"
          title="Add Credit Amount"
      >
        <div class="flex flex-col gap-4">
          <p class="text-sm text-secondary">
            This places a General Account Credit on the customer's account for the amount below.
            It can be applied against any of their future invoices, in full or in part, any
            number of times, until this credit expires on {{ formattedCreditExpiryDate }}.
          </p>

          <div class="flex flex-col">
            <label class="uppercase font-semibold text-sm mb-1">Credit Amount</label>
            <InputNumber v-model="creditAmountInput" mode="currency" currency="GBP" locale="en-GB" :min="0.01"/>
          </div>

          <p v-if="addCreditAmountError" class="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3">
            {{ addCreditAmountError }}
          </p>

          <AppButton color="green" label="Add Credit" :icon="true" @click="submitCreditAmount">
            <template v-slot:icon>
              <Icon iconType="add" class="mr-2"/>
            </template>
          </AppButton>
        </div>
      </panel>
    </portal>

    <portal to="overlay-outlet">
      <panel
          :showing="extendExpiryPanelOpen"
          @close="closeExtendExpiry"
          title="Reopen Expired Credit"
      >
        <div class="flex flex-col gap-4">
          <p class="text-sm text-secondary">
            This credit expired on {{ formattedCreditExpiryDate }} with
            {{ formatCurrency(credit.remaining_balance) }} still unused. Choose a new expiry date and
            say why it is being reopened - the reason is kept on the credit.
          </p>

          <div class="flex flex-col">
            <label class="uppercase font-semibold text-sm mb-1">New Expiry Date <span class="text-red-400">*</span></label>
            <Calendar v-model="extendExpiryDate" dateFormat="dd/mm/yy" :showIcon="true" :minDate="earliestPickableDate"/>
          </div>

          <div class="flex flex-col">
            <label class="uppercase font-semibold text-sm mb-1">Reason <span class="text-red-400">*</span></label>
            <textarea
                v-model="extendExpiryReason"
                class="border rounded bg-gray-100 border-gray-400 hover:border-primary focus:border-primary py-3 px-4 focus:shadow w-full"
                placeholder="Why is this expired credit being reopened?"
                rows="3"
            ></textarea>
          </div>

          <p v-if="extendExpiryError" class="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3">
            {{ extendExpiryError }}
          </p>

          <AppButton color="green" label="Reopen Credit" :icon="true" @click="submitExtendExpiry">
            <template v-slot:icon>
              <Icon iconType="add" class="mr-2"/>
            </template>
          </AppButton>
        </div>
      </panel>
    </portal>

    <portal to="overlay-outlet">
      <panel
          :showing="sendEmailPanelOpen"
          @close="sendEmailPanelOpen = false"
          title="Send Email"
      >
        <SendEmail v-if="credit"
                   :creditId="credit.credit_id"
                   :creditNumber="credit.credit_no"
                   :customer="invoice_to"
                   :credit="credit"/>

      </panel>
    </portal>

    <Confirmation
        ref="voidConfirm"
        title="Void Credit"
        :message="voidMessage"
        :requireReason="true"
        reasonPlaceholder="Reason for voiding..."
    />

  </form>
</template>


<script>
import _ from "lodash";
import PageHeader from "@/components/PageHeader.vue";
import Panel from "@/components/Panel.vue";
import Spinner from "@/components/Spinner.vue";
import ContactAutocomplete from '@/components/AutoComplete/ContactAutoComplete.vue';
import LoanEquipmentAutoComplete from '@/components/AutoComplete/LoanEquipmentAutoComplete.vue';
import AppButton from '@/components/Button.vue';
import AppOutlinedButton from '@/components/OutlinedButton.vue';
import Icon from '@/components/Icon.vue';
import notificationMixin from "@/mixins/notificationMixin";
import ContactInfoCard from "@/components/Contacts/InfoCard.vue";
import OrderItems from "@/views/HR/Credit/Components/OrderItems.vue";
import ProductSummary from "@/views/HR/Credit/Components/ProductSummary.vue";
import SendEmail from "@/views/HR/Credit/Components/SendEmail.vue";
import Confirmation from "@/components/Confirmation.vue";
import printJS from "print-js";

const Toggle = () => import("@/components/Toggle.vue");
const PDFViewer = () => import("@/components/PDFViewer.vue");
export default {
  name: "ManageCredit",
  mixins: [notificationMixin],
  components: {
    PageHeader,
    Panel,
    Spinner,
    ContactAutocomplete,
    LoanEquipmentAutoComplete,
    AppButton,
    Icon,
    Toggle,
    ContactInfoCard,
    OrderItems,
    ProductSummary,
    PDFViewer,
    SendEmail,
    AppOutlinedButton,
    Confirmation,
  },
  props: {
    credit_id: String,
  },
  data() {
    return {
      isBusy: false,
      addCreditAmountPanelOpen: false,
      creditAmountInput: null,
      addCreditAmountError: null,
      extendExpiryPanelOpen: false,
      extendExpiryDate: null,
      extendExpiryReason: "",
      extendExpiryError: null,
      issueChoicePanelOpen: false,
      issuePreview: null,
      credit: {
        credit_date: null,
        credit_expiry_date: null,
        credit_type: null,
        credit_items: [],
        duty: 0.0,
        shipping_cost: 0.0,
        shipping_tax_code: "",
        shipping_tax_rate: 0.0,
        supplier_reference: '',
      },
      sold_to: null,
      invoice_to: null,
      delivery_to: null,
      products: [],
      tax_codes: [],
      pdfViewerData: {
        documentName: "",
        pdfData: null,
        isShowingViewer: false,
      },
      sendEmailPanelOpen: false,
      linkedInvoice: null,
    };
  },

  async mounted() {
    this.isBusy = true;

    if (!this.credit_id) {
      if (this.$store.state.user && this.$store.state.user.staff_id) {
        this.setDefaultCreatedBy();
      }
      this.setDefaultDates();
      await this.prefillSoldToFromQuery();
      this.isBusy = false;
      return;
    }

    if (this.credit_id) {
      await this.loadCreditAndApply();
      return;
    }

    this.isBusy = false;
  },
  watch: {
    // Watch for changes in route parameters
    "$route.params.credit_id": {
      handler: "loadCreditAndApply",
      immediate: true,
    },
  },
  computed: {
    pageTitle() {
      return this.credit_id ? "Update Credit" : "Create Credit";
    },

    getCreditNo() {
      return this.credit && this.credit.credit_no ? this.credit.credit_no : "";
    },

    currentStepButtons() {
      const issueCreditNoteButton = { label: "Issue Credit Note", color: "indigo", action: this.issueCreditNote, icon: "file-invoice-dollar" };
      const showIssueButton = this.isInvoiceCorrectionCredit && !this.isCreditIssued && this.credit.status === "draft";

      const previewButton = { label: "Preview Credit", color: "green", action: this.previewCredit, icon: "search" };
      const emailButton = { label: "Email Credit", color: "green", action: this.sendEmail, icon: "mail" };
      const printButton = { label: "Print Credit", color: "green", action: this.printCredit, icon: "print" };
      const saveButton = {
        label: this.canActivateOpenCredit ? "Save & Activate" : "Save Changes",
        color: "blue",
        action: this.saveCredit,
        icon: "save",
      };
      const extendExpiryButton = { label: "Reopen Credit", color: "indigo", action: this.openExtendExpiry, icon: "restore" };

      const stepButtons = {
        draft: [previewButton, emailButton, printButton, saveButton],
        "under query": [previewButton, emailButton, printButton, saveButton],
        active: [previewButton, emailButton, printButton, saveButton],
        // Once money has moved the lines are locked, but the credit can still be emailed and
        // its internal notes saved
        "partly used": [previewButton, emailButton, printButton, saveButton],
        used: [previewButton, emailButton, printButton, saveButton],
        issued: [previewButton, emailButton, printButton],
        expired: [previewButton, printButton],
        void: [previewButton, printButton],
      };

      // A brand new credit has no status yet and can only be saved
      const buttons = stepButtons[this.credit.status] || [saveButton];

      const extras = [];
      if (showIssueButton) {
        extras.push(issueCreditNoteButton);
      }
      if (this.credit.status === "expired" && !this.isInvoiceCorrectionCredit) {
        extras.push(extendExpiryButton);
      }

      return [...buttons, ...extras];
    },

    proFormaDisplay: {
      get() {
        return this.credit.is_pro_forma ? "YES" : "NO";
      },
      set(value) {
        this.credit.is_pro_forma = value === "YES";
      },
    },

    // Staff can only move a credit between draft, under query and void. Every other status is
    // set by the system from what has happened to the credit: saving an open credit makes it
    // active, applying it makes it partly used or used, issuing a credit note makes it issued,
    // and the expiry date passing makes it expired.
    staffStatusOptions() {
      if (this.isCreditIssued) {
        // An issued credit note can still be voided while its invoice is unpaid: the credited
        // amount goes back onto the invoice. The server refuses once the invoice is paid.
        return this.isInvoiceCorrectionCredit && this.credit.status === "issued" ? ["void"] : [];
      }
      const transitions = {
        draft: ["under query", "void"],
        "under query": ["draft", "void"],
        active: ["under query", "void"],
      };
      return transitions[this.credit.status] || [];
    },

    voidMessage() {
      if (this.isCreditIssued && this.creditNoteApplication) {
        return `This will void the issued credit note and put ${this.formatCurrency(this.creditNoteApplication.amount)} back onto invoice ${this.credit.invoice_no}. Please provide a reason.`;
      }
      return "This will void the credit. It can no longer be edited, issued or applied to an invoice. Please provide a reason.";
    },

    creditStatusItems() {
      const items = {
        draft: { label: "Back to Draft", icon: "pi pi-undo", command: () => this.updateCreditStatus("draft") },
        "under query": { label: "Under Query", icon: "pi pi-question-circle", command: () => this.updateCreditStatus("under query") },
        void: { label: "Void Credit", icon: "pi pi-ban", command: () => this.voidCredit() },
      };
      return this.staffStatusOptions.map((status) => items[status]);
    },

    isInvoiceCorrectionCredit() {
      return this.credit.credit_source === 'invoice_correction';
    },

    isCreditEditable() {
      return ['draft', 'under query'].includes(this.credit.status);
    },

    canAddProducts() {
      return !!this.credit_id
          && !this.isInvoiceCorrectionCredit
          && !this.isCreditIssued
          && this.isCreditEditable;
    },

    isCreditIssued() {
      return !!(this.credit.applications && this.credit.applications.length > 0);
    },

    // Once money has moved against this credit, or it has left draft / under query, its terms
    // are locked. Staff can put it under query to unlock it while nothing has been applied.
    isCreditLocked() {
      return this.isCreditIssued || (!!this.credit_id && !this.isCreditEditable);
    },

    originalInvoiceValue() {
      return this.linkedInvoice ? (this.linkedInvoice.total_value || 0) : 0;
    },

    totalDeductions() {
      return this.credit.total_value || 0;
    },

    outstandingBalance() {
      return this.originalInvoiceValue + this.totalDeductions;
    },

    effectiveCreditSource() {
      if (this.credit.credit_source === 'invoice_correction' || this.credit.credit_source === 'open') {
        return this.credit.credit_source;
      }
      return this.credit.invoice_id ? 'invoice_correction' : 'open';
    },

    // The most this credit note can be for right now: what could be credited when it was started,
    // or what the invoice owes now if that has gone down since - whichever is lower.
    creditNoteLimit() {
      if (!this.isInvoiceCorrectionCredit) {
        return null;
      }

      const limits = [];
      const stored = this.credit.max_credit_amount;
      if (stored !== undefined && stored !== null) {
        limits.push(Number(stored));
      }

      if (this.linkedInvoice && !this.isCreditIssued) {
        const balance = this.linkedInvoice.outstanding_balance;
        const owed = balance !== undefined && balance !== null
            ? balance
            : (this.linkedInvoice.total_value || 0) - (this.linkedInvoice.credited_amount || 0);
        limits.push(Math.max(Number(owed), 0));
      }

      return limits.length > 0 ? Math.min(...limits) : null;
    },

    // Nothing earlier than today can be picked for an open credit's dates
    earliestPickableDate() {
      return this.$moment().startOf("day").toDate();
    },

    // An open credit that is still a draft cannot be applied to any invoice yet
    isDraftOpenCredit() {
      return !!this.credit_id && !this.isInvoiceCorrectionCredit && this.credit.status === "draft";
    },

    canActivateOpenCredit() {
      return this.isDraftOpenCredit && Math.abs(this.credit.total_value || 0) > 0;
    },

    formattedCreditExpiryDate() {
      // credit.credit_expiry_date is a JS Date for a brand new credit, but already a
      // "DD-MM-YYYY" string once loaded from the server (see convertCreditDatesFromUnix) -
      // moment needs the explicit format to parse that string form correctly.
      return this.credit.credit_expiry_date ? this.$moment(this.credit.credit_expiry_date, "DD-MM-YYYY").format("DD-MM-YYYY") : "the date set above";
    },

    creditNoteApplication() {
      return this.credit.applications && this.credit.applications.length > 0 ? this.credit.applications[0] : null;
    },

    creditNoteRequestedAmount() {
      return Math.abs(this.credit.total_value || 0);
    },

    creditNoteAppliedAmountReduced() {
      if (!this.creditNoteApplication) {
        return false;
      }
      const appliedCents = Math.round(this.creditNoteApplication.amount * 100);
      const requestedCents = Math.round(this.creditNoteRequestedAmount * 100);
      return appliedCents < requestedCents;
    },

    openCreditApplicationsSorted() {
      return (this.credit.applications || []).slice().sort((a, b) => (b.applied_at || 0) - (a.applied_at || 0));
    },

    openCreditApplicationsTotal() {
      return (this.credit.applications || []).reduce((sum, application) => sum + (application.amount || 0), 0);
    },
  },

  methods: {
    getStatusClass(status) {
      if (!status) {
        return "bg-gray-100 text-gray-500";
      }

      switch (status.toLowerCase()) {
        case "draft":
          return "bg-gray-200 text-gray-700";
        case "under query":
          return "bg-yellow-200 text-yellow-700";
        case "active":
        case "partly used":
          return "bg-blue-200 text-blue-700";
        case "used":
        case "issued":
          return "bg-green-200 text-green-700";
        case "expired":
        case "void":
          return "bg-red-200 text-red-700";
        default:
          return "bg-gray-100 text-gray-500";
      }
    },

    async loadCreditAndApply() {
      this.isBusy = true;
      const credit = await this.loadCreditById();
      this.credit = await this.formatCreditData(credit);

      if (this.effectiveCreditSource === 'invoice_correction' && this.credit.invoice_id) {
        this.linkedInvoice = await this.InvoiceService.getInvoiceById(this.credit.invoice_id);
      }

      this.isBusy = false;
    },

    async handleCreditItemChanged() {
      await this.loadCreditAndApply();
    },

    updateCreditValue(field, value) {
      this.credit[field] = value;
    },

    sendEmail() {
      this.sendEmailPanelOpen = true;
    },

    async addNewContact() {
      await this.$router.push({
        name: "contacts",
      });
    },

    showAddCreditAmount() {
      this.creditAmountInput = null;
      this.addCreditAmountError = null;
      this.addCreditAmountPanelOpen = true;
    },

    openExtendExpiry() {
      this.extendExpiryDate = null;
      this.extendExpiryReason = "";
      this.extendExpiryError = null;
      this.extendExpiryPanelOpen = true;
    },

    closeExtendExpiry() {
      this.extendExpiryPanelOpen = false;
      this.extendExpiryError = null;
    },

    async submitExtendExpiry() {
      this.extendExpiryError = null;

      if (!this.extendExpiryDate) {
        this.extendExpiryError = "Please choose a new expiry date.";
        return;
      }
      if (!this.extendExpiryReason || !this.extendExpiryReason.trim()) {
        this.extendExpiryError = "Please give a reason for reopening this credit.";
        return;
      }

      this.isBusy = true;
      try {
        await this.CreditService.extendCreditExpiry(
            this.credit_id,
            this.$moment(this.extendExpiryDate).format("YYYY-MM-DD"),
            this.extendExpiryReason.trim(),
        );
        this.closeExtendExpiry();
        this.notifySuccess("Credit reopened");
        await this.loadCreditAndApply();
      } catch (error) {
        const serverMessage = error && error.data && error.data.message;
        this.extendExpiryError = serverMessage ? String(serverMessage) : "Couldn't reopen the credit. Please try again.";
      } finally {
        this.isBusy = false;
      }
    },

    closeAddCreditAmount() {
      this.addCreditAmountPanelOpen = false;
      this.creditAmountInput = null;
      this.addCreditAmountError = null;
    },

    async submitCreditAmount() {
      this.addCreditAmountError = null;

      const amount = parseFloat(this.creditAmountInput);
      if (!amount || amount <= 0) {
        this.addCreditAmountError = "Please enter a credit amount greater than zero.";
        return;
      }

      // The line item still has to be a real product from the catalog - "CREDIT" is the
      // General Account Credit product it's meant to use. If it hasn't been set up in the
      // products database yet, this fails with a clear message rather than fabricating one.
      let creditProduct;
      try {
        creditProduct = await this.ProductService.getProductByCode("CREDIT");
      } catch (error) {
        this.addCreditAmountError = "The 'CREDIT' product hasn't been set up in the products catalog yet - ask the product team to add it before this can be used.";
        return;
      }

      const confirmation = await this.confirmAction({
        message: `Add a £${amount.toFixed(2)} General Account Credit to this customer's account? This will be available to apply against their future invoices.`,
        header: "Confirm Add Credit",
        acceptLabel: "Yes",
        rejectLabel: "No",
      });

      if (!confirmation) {
        return;
      }

      this.closeAddCreditAmount();

      await this.$refs.orderItemRef.addProducts([{
        ...creditProduct,
        description: creditProduct.name,
        cost_price: -amount,
        tax_code: "T0",
        tax_rate: 0,
        quantity: 1,
      }]);
      await this.loadCreditAndApply();
    },

    setDefaultDates() {
      if (!this.credit.credit_date) {
        this.credit.credit_date = this.$moment().toDate();
      }
      if (!this.credit.credit_expiry_date) {
        this.credit.credit_expiry_date = this.$moment().add(30, "days").toDate();
      }
    },

    setDefaultCreatedBy() {
      this.credit.created_by = {
        name: `${this.$store.state.user.first_name} ${this.$store.state.user.last_name}`,
        staff_id: this.$store.state.user.staff_id,
      };
    },

    convertCreditDatesFromUnix(credit) {
      const convertUnixToDate = (unixTimestamp) => {
        return this.$moment.unix(unixTimestamp).format("DD-MM-YYYY");
      };

      if (credit.credit_date) {
        credit.credit_date = convertUnixToDate(credit.credit_date);
      }

      if (credit.credit_expiry_date) {
        credit.credit_expiry_date = convertUnixToDate(credit.credit_expiry_date);
      }

      return credit;
    },

    async fetchDataMasterTaxCodes() {
      try {
        const response = await this.DatamasterService.getDataMasters(
            "?noPaging=true&type=PO_Tax_Code&is_active=true&orderBy=name&order=1"
        );
        this.tax_codes = response.map((item) => ({
          data_master_id: item.data_master_id,
          name: item.name,
          value: item.value || "",
        }));
      } catch (error) {
        console.error("Error fetching data master certificates:", error);
      }
    },

    async formatCreditData(credit) {
      let formattedCredit = JSON.parse(JSON.stringify(credit));

      formattedCredit = this.convertCreditDatesFromUnix(formattedCredit);

      function findDefault(items) {
        return items && items.length > 0
            ? items.find((item) => item.is_default) || null
            : null;
      }

      if (
          formattedCredit.sold_to_contact &&
          formattedCredit.sold_to_contact.contact_id &&
          formattedCredit.sold_to_contact.name
      ) {
        this.sold_to = {
          name: formattedCredit.sold_to_contact.name,
          contact_id: formattedCredit.sold_to_contact.contact_id,
        };

        this.sold_to.address = findDefault(formattedCredit.sold_to_contact.addresses);

        this.sold_to.people = findDefault(formattedCredit.sold_to_contact.people);
      }

      if (
          formattedCredit.invoice_to_contact &&
          formattedCredit.invoice_to_contact.contact_id &&
          formattedCredit.invoice_to_contact.name
      ) {
        this.invoice_to = {
          name: formattedCredit.invoice_to_contact.name,
          contact_id: formattedCredit.invoice_to_contact.contact_id,
        };

        this.invoice_to.address = findDefault(formattedCredit.invoice_to_contact.addresses);

        this.invoice_to.people = findDefault(formattedCredit.invoice_to_contact.people);
      }

      if (
          formattedCredit.delivery_to_contact &&
          formattedCredit.delivery_to_contact.contact_id &&
          formattedCredit.delivery_to_contact.name
      ) {
        this.delivery_to = {
          name: formattedCredit.delivery_to_contact.name,
          contact_id: formattedCredit.delivery_to_contact.contact_id,
        };

        this.delivery_to.address = findDefault(formattedCredit.delivery_to_contact.addresses);

        this.delivery_to.people = findDefault(formattedCredit.delivery_to_contact.people);
      }

      formattedCredit.credit_items = await this.convertToOrderItems(formattedCredit.items);

      return formattedCredit;
    },

    async convertToOrderItems(dbProducts) {
      if (!dbProducts || dbProducts.length === 0) {
        return [];
      }

      await this.fetchDataMasterTaxCodes();

      return dbProducts.map((product) => {
        return {
          product_id: product.product_id,
          credit_item_id: product.credit_item_id,
          sales_item_id: product.sales_item_id,
          invoice_item_id: product.invoice_item_id,
          product_code: product.product_code,
          supplier_product_code: product.supplier_product_code,
          quantity: product.quantity.toString(),
          price: product.unit_price,
          tax_code: product.tax_code,
          tax_rate: product.tax_rate,
          total: product.total,
          total_with_tax: product.total_with_tax,
          description: product.description,
          original_quantity: product.original_quantity,
          original_unit_price: product.original_unit_price,
          original_total: product.original_total,
          original_total_with_tax: product.original_total_with_tax,
          credit_status: product.credit_status,
          tax_codes: this.tax_codes,
          received_items: product.received_items || [],
        };
      });
    },

    async prefillSoldToFromQuery() {
      const contactId = this.$route.query.sold_to;
      if (!contactId) {
        return;
      }

      try {
        // Use the search endpoint (not getContactById) so the contact comes back in the
        // reduced { address, people } shape that ContactInfoCard and handleSoldToSelected expect —
        // matching what ContactAutocomplete provides on a manual selection.
        const results = await this.ContactService.searchContactsWithType({ contact_id: contactId });
        const contact = results && results.length > 0 ? results[0] : null;
        if (contact && contact.contact_id) {
          this.sold_to = contact;
          await this.handleSoldToSelected({ value: contact });
        }
      } catch (error) {
        console.error("Error prefilling contact from query:", error);
      }
    },

    handleSoldToSelected: async function (selectedCustomer) {
      const customerData = selectedCustomer.value;

      if (customerData && customerData.agent_id) {
        try {
          const response = await this.ContactService.searchContactsWithType({
            contact_id: customerData.agent_id,
          });

          // Fall back to SOLD TO if the assigned agent can't be resolved (e.g. stale agent_id)
          const assignedAgent = response && response.length > 0 ? response[0] : customerData;
          this.invoice_to = _.cloneDeep(assignedAgent);
        } catch (error) {
          console.error("Error fetching agent details: ", error);
          this.invoice_to = _.cloneDeep(customerData);
        }
      } else {
        this.invoice_to = _.cloneDeep(customerData);
      }

      // DELIVERY TO always defaults to SOLD TO
      this.delivery_to = _.cloneDeep(customerData);
    },

    handleSoldToCleared(selectedContact) {
      this.sold_to = null;
      this.invoice_to = null;
      this.delivery_to = null;
    },

    handleInvoiceToSelected(selectedCustomer) {},

    handleInvoiceToCleared(selectedContact) {},

    handleDeliveryToSelected(selectedCustomer) {},

    handleDeliveryToCleared(selectedContact) {},

    async loadCreditById() {
      try {
        const creditData = await this.CreditService.getCreditById(this.credit_id);

        if (!creditData) {
          return;
        }
        return creditData;
      } catch (error) {
        this.isBusy = false;
        console.error("Error loading credit:", error);
      }
    },

    convertCreditDatesToUnix(credit) {
      const convertDateToUnix = (date) => {
        if (date instanceof Date) {
          return Math.floor(date.getTime() / 1000);
        } else if (typeof date === "string") {
          const momentDate = this.$moment(date, "DD-MM-YYYY", true);
          if (momentDate.isValid()) {
            return momentDate.unix();
          } else {
            const isoMomentDate = this.$moment(date, this.$moment.ISO_8601);
            if (isoMomentDate.isValid()) {
              return isoMomentDate.unix();
            }
          }
        }
        console.error("Invalid or unsupported date format:", date);
        return null;
      };

      if (credit.credit_date) {
        credit.credit_date = convertDateToUnix(credit.credit_date);
      }

      if (credit.credit_expiry_date) {
        credit.credit_expiry_date = convertDateToUnix(credit.credit_expiry_date);
      }

      return credit;
    },

    getCreditData(credit) {
      let convertedCredit = JSON.parse(JSON.stringify(credit));

      convertedCredit = this.convertCreditDatesToUnix(convertedCredit);

      if (this.sold_to && this.sold_to.contact_id) {
        convertedCredit.sold_to = this.sold_to.contact_id;
      }
      if (this.invoice_to && this.invoice_to.contact_id) {
        convertedCredit.invoice_to = this.invoice_to.contact_id;
      }
      if (this.delivery_to && this.delivery_to.contact_id) {
        convertedCredit.delivery_to = this.delivery_to.contact_id;
      }

      convertedCredit.duty = parseFloat(this.credit.duty) || 0;
      convertedCredit.shipping_cost = parseFloat(this.credit.shipping_cost) || 0;
      convertedCredit.shipping_tax_code = this.credit.shipping_tax_code;
      convertedCredit.shipping_tax_rate = parseFloat(this.credit.shipping_tax_rate) || 0;

      return convertedCredit;
    },

    getOrderItems() {
      return this.$refs.orderItemRef ? this.$refs.orderItemRef.getCurrentProducts() : [];
    },

    closeViewer() {
      this.pdfViewerData = {
        pdfData: null,
        documentName: "",
        isShowingViewer: false,
      };
    },

    async previewCredit() {
      if (!this.credit_id) {
        this.notifyWarning("Missing credit ID. Please ensure all required fields are filled.");
        return;
      }

      try {
        const response = await this.CreditService.generateCreditReport(this.credit_id, "credit");

        this.pdfViewerData = {
          pdfData: response,
          documentName: "Credit Report",
          isShowingViewer: true,
        };
      } catch (error) {
        console.error("Error loading PDF:", error);
        this.notifyWarning("Failed to load the PDF document. Please try again.");
      }
    },

    async printCredit() {
      if (!this.credit_id) {
        this.notifyWarning("Missing credit ID. Please ensure all required fields are filled.");
        return;
      }

      try {
        const response = await this.CreditService.generateCreditReport(this.credit_id, "credit");

        this.pdfViewerData = {
          pdfData: response,
          documentName: "Credit Report",
          isShowingViewer: true,
        };
      } catch (error) {
        console.error("Error loading PDF:", error);
        this.notifyWarning("Failed to load the PDF document. Please try again.");
      }
    },

    validateRequiredFields() {
      const requiredFields = [
        { field: this.sold_to, name: "Sold To" },
        { field: this.invoice_to, name: "Invoice To" },
        { field: this.credit.credit_type, name: "Credit Type" },
        { field: this.credit.credit_date, name: "Credit Date" },
      ];

      // A credit note from an invoice has no expiry date to fill in
      if (!this.isInvoiceCorrectionCredit) {
        requiredFields.push({ field: this.credit.credit_expiry_date, name: "Credit Expiry Date" });
      }

      for (const field of requiredFields) {
        if (!field.field) {
          this.notifyError(`${field.name} is required.`);
          return false;
        }
      }
      return true;
    },

    async saveCredit() {
      if (!this.validateRequiredFields()) {
        return false;
      }

      this.isBusy = true;
      const formattedCredit = this.getCreditData(this.credit);
      try {
        if (this.credit_id) {
          await this.CreditService.updateCredit(this.credit_id, formattedCredit);
          this.notifySuccess("Credit updated successfully");
          await this.loadCreditAndApply();
        } else {
          const response = await this.CreditService.createCredit(formattedCredit);
          this.credit_id = response.credit_id;
          // replace, not push - so Back from the saved credit doesn't land on the empty
          // "new credit" form it was created from
          await this.$router.replace({
            name: "credit-edit",
            params: { credit_id: this.credit_id },
          });
          this.notifySuccess("Credit created successfully");
        }
        return true;
      } catch (error) {
        const errorMessage = this.credit_id
            ? "Couldn't update Credit"
            : "Couldn't create Credit";
        const serverMessage = error && error.data && error.data.message;
        this.notifyError(serverMessage ? String(serverMessage) : errorMessage);
        console.error(error);
        return false;
      } finally {
        this.isBusy = false;
      }
    },

    submitCredit: async function (event) {
      event.preventDefault();
    },

    formatCurrency(value) {
      const amount = parseFloat(value) || 0;
      const formatted = Math.abs(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return amount < 0 ? `- £${formatted}` : `£${formatted}`;
    },

    async issueCreditNote() {
      // Save what is on the form first, so a credit type or note chosen just now is on the credit
      // before it is issued. Stops here if a required field is still empty.
      const saved = await this.saveCredit();
      if (!saved) {
        return;
      }

      let preview;
      this.isBusy = true;
      try {
        preview = await this.CreditService.previewFinalizeCredit(this.credit_id);
      } catch (error) {
        this.notifyError(error && error.data ? error.data.message : "Couldn't check the credit note before issuing");
        console.error(error);
        return;
      } finally {
        this.isBusy = false;
      }

      if (preview.hasDiscrepancy) {
        // The invoice no longer owes what this credit note was created for - let the user
        // choose how to resolve it, rather than deciding for them.
        this.issuePreview = preview;
        this.issueChoicePanelOpen = true;
        return;
      }

      const confirmation = await this.confirmAction({
        message: `Are you sure you want to issue this credit note for ${this.formatCurrency(preview.requestedAmount)}? `
            + `This will apply the credited amount back to the original invoice and cannot be undone.`,
        header: "Confirm Issue Credit Note",
        acceptLabel: "Yes",
        rejectLabel: "No",
      });

      if (!confirmation) {
        return;
      }

      await this.doIssueCreditNote(true);
    },

    async doIssueCreditNote(matchCurrentOutstanding) {
      this.issueChoicePanelOpen = false;
      this.isBusy = true;
      try {
        await this.CreditService.finalizeCredit(this.credit_id, matchCurrentOutstanding);
        this.notifySuccess("Credit note issued successfully");
        await this.loadCreditAndApply();
      } catch (error) {
        this.notifyError(error && error.data ? error.data.message : "Couldn't issue the credit note");
        console.error(error);
      } finally {
        this.isBusy = false;
      }
    },

    async updateCreditStatus(status) {
      const previousStatus = this.credit.status;
      this.credit.status = status;
      const saveSuccessful = await this.saveCredit();
      if (!saveSuccessful) {
        this.credit.status = previousStatus;
      }
    },

    async voidCredit() {
      const reason = await this.$refs.voidConfirm.open();
      if (!reason) {
        return;
      }

      this.isBusy = true;
      try {
        await this.CreditService.voidCredit(this.credit_id, reason);
        this.notifySuccess("Credit voided");
        await this.loadCreditAndApply();
      } catch (error) {
        const serverMessage = error && error.data && error.data.message;
        this.notifyError(serverMessage ? String(serverMessage) : "Couldn't void the credit");
        console.error(error);
      } finally {
        this.isBusy = false;
      }
    },
  }

}
;
</script>

<style>
.hide-header .p-datatable-thead {
  display: none;
}

</style>

<style scoped>
.p-component-overlay {
  background-color: rgba(0, 0, 0, 0.7) !important;
}

::v-deep .status-split-button .p-splitbutton-defaultbutton,
::v-deep .status-split-button .p-splitbutton-menubutton {
  background-color: #bee3f8 !important;
  border-color: #63b3ed !important;
  color: #383E42 !important;
  box-shadow: none !important;
  transition: all 300ms ease-in-out !important;
}

::v-deep .status-split-button .p-splitbutton-defaultbutton:hover,
::v-deep .status-split-button .p-splitbutton-menubutton:hover {
  background-color: #90cdf4 !important;
  border-color: #4299e1 !important;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06) !important;
}

::v-deep .status-split-button .p-splitbutton-defaultbutton:active,
::v-deep .status-split-button .p-splitbutton-menubutton:active {
  box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.06) !important;
}

::v-deep .status-split-button .p-splitbutton-defaultbutton {
  border-top-left-radius: 0.375rem !important;
  border-bottom-left-radius: 0.375rem !important;
}

::v-deep .status-split-button .p-splitbutton-menubutton {
  border-top-right-radius: 0.375rem !important;
  border-bottom-right-radius: 0.375rem !important;
}
</style>